import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { getSurfaceColorProps } from "../../utils/colorValue";
import { collection, query, where, orderBy, limit, startAfter, getDocs } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db } from "../../firebaseConfig";
import { buildStreetLayout, isNearStreetFrontier, mergeStreetUsers, MUULTI_GREEN, resolveSurface } from "../../utils/muulti3dLayout";
import { QUALITY_PRESETS, SceneDialog, SceneText, SceneViewport, SceneWallBand, updateSurfaceMaterial, useNearbyIndices, useSceneLighting, useSceneQuality, useSurfaceTexture } from "./Muulti3DShared";
import "../MuultiStyle.css";
import "./Muulti3D.css";

const PAGE_SIZE = 24;
const NIGHT_SKY = "#07151d";
const directoryRequests = new Map();
let streetPose = { x: 0, z: 0, yaw: -Math.PI / 2, pitch: 0 };

function readDirectoryPage(cursor = null) {
    const key = cursor?.id || "first";
    if (!directoryRequests.has(key)) {
        const constraints = [where("muultiName", "!=", ""), orderBy("muultiName"), limit(PAGE_SIZE)];
        if (cursor) constraints.push(startAfter(cursor));
        const request = getDocs(query(collection(db, "users"), ...constraints))
            .finally(() => directoryRequests.delete(key));
        directoryRequests.set(key, request);
    }
    return directoryRequests.get(key);
}

// Batch all street or block rectangles into one draw call per surface.
function GroundLayer({ rectangles, name, color, height }) {
    const geometry = useMemo(() => {
        const positions = [], indices = [];
        rectangles.forEach(({ position, width, depth }) => {
            const left = position[0] - width / 2, right = position[0] + width / 2;
            const top = position[2] - depth / 2, bottom = position[2] + depth / 2;
            const offset = positions.length / 3;
            positions.push(left, 0, top, left, 0, bottom, right, 0, bottom, right, 0, top);
            indices.push(offset, offset + 1, offset + 2, offset, offset + 2, offset + 3);
        });
        const result = new THREE.BufferGeometry();
        result.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
        result.setIndex(indices);
        result.computeVertexNormals();
        return result;
    }, [rectangles]);
    useEffect(() => () => geometry.dispose(), [geometry]);
    return <mesh name={name} position-y={height} geometry={geometry} receiveShadow>
        <meshStandardMaterial color={color} roughness={0.95} />
    </mesh>;
}

function Ground({ layout }) {
    return <>
        <mesh name="street-ground" rotation-x={-Math.PI / 2} position={[layout.centerX, -0.04, layout.centerZ]} receiveShadow>
            <planeGeometry args={[layout.width, layout.depth]} />
            <meshStandardMaterial color={MUULTI_GREEN} roughness={0.95} />
        </mesh>
        <GroundLayer rectangles={layout.blocks} name="street-blocks" color="#2c5d50" height={-0.02} />
        <GroundLayer rectangles={layout.streets} name="street-path" color={MUULTI_GREEN} height={0} />
    </>;
}

function StreetLight({ playerPosRef, quality, shadowsOn, lightingOn }) {
    const lightRef = useRef(null);
    const moonRef = useRef(null);
    const target = useMemo(() => new THREE.Object3D(), []);
    useFrame(() => {
        const { x, z } = playerPosRef.current;
        // Follow position, not look direction, so the moon stays distant along the whole street.
        if (moonRef.current) moonRef.current.position.set(x + 160, 70, z + 30);
        if (!lightRef.current) return;
        lightRef.current.position.set(x + 64, 28, z + 12);
        target.position.set(x, 0, z);
        target.updateMatrixWorld();
    });
    return <>
        <mesh ref={moonRef} name="street-moon" position={[playerPosRef.current.x + 160, 70, playerPosRef.current.z + 30]}>
            <sphereGeometry args={[6, 24, 16]} />
            <meshBasicMaterial color="#e3f5df" fog={false} toneMapped={false} />
        </mesh>
        <primitive object={target} />
        {lightingOn && <directionalLight name="street-moonlight" key={quality.shadowMapSize} ref={lightRef} target={target}
            position={[playerPosRef.current.x + 64, 28, playerPosRef.current.z + 12]} color="#c3d9e8" intensity={0.85}
            castShadow={quality.shadows && shadowsOn}
            shadow-mapSize-width={quality.shadowMapSize} shadow-mapSize-height={quality.shadowMapSize}
            shadow-camera-left={-55} shadow-camera-right={55} shadow-camera-top={45} shadow-camera-bottom={-45}
            shadow-camera-far={180} shadow-bias={-0.0005} />}
    </>;
}

// Fit long titles and descriptions inside the board without scrolling or cutting off text.
function BillboardText({ children, fontSize, maxWidth, maxHeight, ...props }) {
    const [scale, setScale] = useState(1);
    useEffect(() => setScale(1), [children, fontSize, maxWidth, maxHeight]);
    const fitText = useCallback(text => {
        const bounds = text.textRenderInfo?.blockBounds;
        const height = bounds ? bounds[3] - bounds[1] : 0;
        if (height > maxHeight + 0.01) setScale(current => current * maxHeight / height);
    }, [maxHeight]);
    return <SceneText {...props} fontSize={fontSize * scale} maxWidth={maxWidth}
        overflowWrap="break-word" onSync={fitText}>{children}</SceneText>;
}

function BillboardSpotlight({ x, height, lightingOn, enabled }) {
    const target = useMemo(() => new THREE.Object3D(), []);
    const position = [0, height / 2 + 0.65, 2.8];
    const tilt = Math.PI - Math.atan2(position[1] + height * 0.1, position[2] - 0.18);
    return <group name="billboard-light-fixture" position-x={x}>
        <mesh position={[0, position[1], 1.25]}>
            <boxGeometry args={[0.12, 0.12, 3.1]} />
            <meshStandardMaterial color={MUULTI_GREEN} metalness={0.6} roughness={0.4} />
        </mesh>
        <group position={position} rotation-x={tilt}>
            <mesh><boxGeometry args={[0.8, 0.38, 0.5]} /><meshStandardMaterial color={MUULTI_GREEN} metalness={0.6} roughness={0.4} /></mesh>
            <mesh name="billboard-lamp" position-z={0.26}>
                <circleGeometry args={[0.27, 12]} />
                <meshBasicMaterial color={lightingOn ? "#fff2ce" : "#526865"} toneMapped={false} />
            </mesh>
        </group>
        <primitive object={target} position={[0, -height * 0.1, 0.18]} />
        {enabled && <spotLight name="billboard-spotlight" position={position} target={target}
            color="#fff2ce" intensity={100} distance={height + 6} angle={Math.PI / 3}
            penumbra={0.7} decay={2} castShadow={false} />}
    </group>;
}

function BuildingBillboard({ user, room, header, quality, textured, lightingOn, illuminated, onOpen, besideDoor = false }) {
    const width = Math.min(22, room.side - 2), height = width * 0.33;
    const postHeight = 1.8;
    const innerWidth = width - 1.2, imageWidth = innerWidth * 0.3;
    const bodyHeight = height - 2.7, bodyY = -height / 2 + 0.55 + bodyHeight / 2;
    const alignment = user.alignment || "left";
    const imageX = (alignment === "right" ? 1 : -1) * (innerWidth - imageWidth) / 2;
    const descriptionWidth = innerWidth - imageWidth - 0.65;
    const descriptionLeft = -innerWidth / 2 + (alignment === "right" ? 0 : imageWidth + 0.65);
    const descriptionX = descriptionLeft + (alignment === "right" ? descriptionWidth : alignment === "center" ? descriptionWidth / 2 : 0);
    const size = Math.min(1024, quality.textureSize);
    const portrait = useMemo(() => resolveSurface({ type: "image", imageUrl: user.profilePicUrl,
        sizing: "contain", fallback: "rgba(0,0,0,0)" }), [user.profilePicUrl]);
    const headerTexture = useSurfaceTexture(header, size, textured, [width, height]);
    const portraitTexture = useSurfaceTexture(portrait, size, textured, [imageWidth, bodyHeight]);
    // Scale the entrance copy of the same board so both signs share cached image textures.
    const doorX = room.door.position[0], direction = doorX <= 0 ? 1 : -1;
    const doorEdge = doorX + direction * 2, wallEdge = direction * (room.half - 0.6);
    const entranceScale = Math.min(0.42, Math.abs(wallEdge - doorEdge) / (width + 0.3));
    const position = besideDoor ? [doorEdge + direction * (width + 0.3) * entranceScale / 2, 2.3, room.half + 0.12]
        : [0, room.height + postHeight + height / 2, room.half + 0.3];
    return <group name={besideDoor ? "building-entrance-header" : "building-billboard"} position={position}
        scale={besideDoor ? entranceScale : 1} onClick={event => event.stopPropagation()}>
        {!besideDoor && <mesh name="billboard-post" position={[0, -height / 2 - postHeight / 2, -0.35]} castShadow>
            <cylinderGeometry args={[0.3, 0.42, postHeight + 0.2, 8]} />
            <meshStandardMaterial color={MUULTI_GREEN} metalness={0.6} roughness={0.4} />
        </mesh>}
        <mesh name="billboard-backing" castShadow>
            <boxGeometry args={[width + 0.3, height + 0.3, 0.3]} />
            <meshStandardMaterial color={MUULTI_GREEN} roughness={0.7} />
        </mesh>
        <group name="building-info-panel" position-z={0.17} onClick={onOpen}>
            <mesh name="building-header-background" receiveShadow>
                <planeGeometry args={[width, height]} />
                <meshStandardMaterial onUpdate={updateSurfaceMaterial} map={headerTexture} {...getSurfaceColorProps(header, headerTexture)}
                    emissiveMap={headerTexture} emissive={getSurfaceColorProps(header, headerTexture).color} emissiveIntensity={0.12} roughness={0.9} />
            </mesh>
            <group name="building-title" position={[alignment === "right" ? innerWidth / 2 : alignment === "center" ? 0 : -innerWidth / 2, height / 2 - 0.55, 0.03]}>
                <BillboardText fontSize={0.62} maxWidth={innerWidth} maxHeight={1.3}
                    color={header.fontColor} anchorX={alignment} anchorY="top" textAlign={alignment}>
                    {String(user.pageTitle)}
                </BillboardText>
            </group>
            <group name="building-image" position={[imageX, bodyY, 0.03]}>
                <mesh>
                    <planeGeometry args={[imageWidth, bodyHeight]} />
                    <meshBasicMaterial onUpdate={updateSurfaceMaterial} map={portraitTexture} color={portraitTexture ? "#ffffff" : MUULTI_GREEN}
                        transparent={!!portraitTexture} />
                </mesh>
                {!portraitTexture && <BillboardText position={[0, 0, 0.02]} fontSize={0.38}
                    maxWidth={imageWidth - 0.4} maxHeight={bodyHeight - 0.4} color="#ffffff"
                    anchorX="center" anchorY="middle" textAlign="center">{String(user.pageTitle)}</BillboardText>}
            </group>
            <group name="building-description" position={[descriptionX, bodyY, 0.03]}>
                <BillboardText fontSize={0.34} maxWidth={descriptionWidth} maxHeight={bodyHeight}
                    color={header.fontColor} anchorX={alignment} anchorY="middle" textAlign={alignment} lineHeight={1.25}>
                    {String(user.pageDescription || "Welcome to " + user.pageTitle)}
                </BillboardText>
            </group>
        </group>
        {!besideDoor && [-width * 0.27, width * 0.27].map(x => <BillboardSpotlight key={x} x={x} height={height}
            lightingOn={lightingOn} enabled={lightingOn && illuminated && textured} />)}
    </group>;
}

function Building({ entry, quality, textured, lightingOn, illuminated, onOpenUser, onEnter }) {
    const { user, room } = entry;
    const skin = useMemo(() => resolveSurface(user.background), [user.background]);
    const header = useMemo(() => ({ ...resolveSurface(user.headerBackground, "#ffffff"), fontColor: user.headerBackground?.fontColor || "#000000" }), [user.headerBackground]);
    const size = Math.min(1024, quality.textureSize);
    const facadeTexture = useSurfaceTexture(skin, size, textured, [room.side, room.height]);
    const headerTexture = useSurfaceTexture(header, size, textured);
    const frontZ = room.half + 0.06;
    const open = event => { event.stopPropagation(); onOpenUser(user); };
    return <group position={entry.position} rotation={entry.rotation}>
        {/* The opaque shell blocks clicks from reaching signs on buildings behind it. */}
        <mesh name="building-shell" position={[0, room.height / 2, 0]} castShadow receiveShadow onClick={event => event.stopPropagation()}>
            <boxGeometry args={[room.side, room.height, room.side]} />
            <meshStandardMaterial onUpdate={updateSurfaceMaterial} map={facadeTexture} {...getSurfaceColorProps(skin, facadeTexture)} roughness={0.85} />
        </mesh>
        <SceneWallBand half={room.half} height={room.height} surface={header} texture={headerTexture} />
        <BuildingBillboard user={user} room={room} header={header} quality={quality} textured={textured}
            lightingOn={lightingOn} illuminated={illuminated} onOpen={open} />
        <BuildingBillboard user={user} room={room} header={header} quality={quality} textured={textured}
            besideDoor onOpen={open} />
        <group name="building-door" position={[room.door.position[0], 2.05, frontZ + 0.02]} onClick={event => { event.stopPropagation(); onEnter(user); }}>
            <mesh><planeGeometry args={[2.8, 4.1]} /><meshBasicMaterial color={MUULTI_GREEN} /></mesh>
            <mesh position={[0, 0, 0.02]}><planeGeometry args={[2.5, 3.8]} /><meshBasicMaterial color="#7ed786" /></mesh>
            <SceneText position={[0, 0.2, 0.04]} fontSize={0.3} color={MUULTI_GREEN}>ENTER</SceneText>
            <mesh position={[0.85, -0.3, 0.05]}><circleGeometry args={[0.09, 12]} /><meshBasicMaterial color="#ffffff" /></mesh>
        </group>
    </group>;
}

function StreetDirectoryPrefetch({ enabled, frontiers, playerPosRef, distance, onNeedMore }) {
    const elapsed = useRef(0);
    useFrame((_, dt) => {
        elapsed.current += dt;
        if (!enabled || elapsed.current < 0.5) return;
        elapsed.current = 0;
        if (isNearStreetFrontier(frontiers, playerPosRef.current, distance)) onNeedMore();
    });
    return null;
}

function StreetBuildings({ layout, quality, playerPosRef, lightingOn, onOpenUser, onEnter }) {
    const items = useMemo(() => layout.entries.map(entry => ({
        position: entry.position, radius: entry.room.half,
    })), [layout.entries]);
    const visible = useNearbyIndices(items, playerPosRef, quality.viewDistance, quality.buildingLimit);
    const textured = useNearbyIndices(items, playerPosRef, quality.textureDistance, quality.textureLimit);
    // Four nearby spotlights at most; distant billboards keep their readable base illumination.
    const illuminated = useNearbyIndices(items, playerPosRef, Math.min(80, quality.textureDistance), 2);
    return visible.map(index => {
        const entry = layout.entries[index];
        return entry && <Building key={entry.user.id} entry={entry} quality={quality}
            textured={textured.includes(index)} lightingOn={lightingOn} illuminated={illuminated.includes(index)}
            onOpenUser={onOpenUser} onEnter={onEnter} />;
    });
}

export default function MuultiStreet3DPage() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [cursor, setCursor] = useState(null);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [retryKey, setRetryKey] = useState(0);
    const [selected, setSelected] = useState(null);
    const [search, setSearch] = useState("");
    const [qualityKey, setQualityKey] = useSceneQuality();
    const [lightingOn, setLightingOn] = useSceneLighting();
    const [shadowsEnabled, setShadowsEnabled] = useState(true);
    const navigate = useNavigate();
    const poseRef = useRef({ ...streetPose });
    const playerPosRef = useRef(new THREE.Vector3(streetPose.x, 0, streetPose.z));
    const teleportRef = useRef(null);
    const loadingMoreRef = useRef(false);
    const mounted = useRef(false);

    useEffect(() => {
        mounted.current = true;
        const pose = poseRef.current;
        return () => { mounted.current = false; streetPose = { ...pose }; };
    }, []);
    const applySnapshot = useCallback(snapshot => {
        setUsers(previous => mergeStreetUsers(previous, snapshot.docs));
        setCursor(snapshot.docs[snapshot.docs.length - 1] || null);
        setHasMore(snapshot.docs.length === PAGE_SIZE);
    }, []);
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError("");
        readDirectoryPage().then(snapshot => {
            if (!cancelled) applySnapshot(snapshot);
        }).catch(() => { if (!cancelled) setError("The street could not be loaded. Please try again."); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [applySnapshot, retryKey]);
    const loadMore = useCallback(async () => {
        if (loadingMoreRef.current || !cursor) return;
        loadingMoreRef.current = true;
        setLoadingMore(true);
        setError("");
        try {
            const snapshot = await readDirectoryPage(cursor);
            if (mounted.current) applySnapshot(snapshot);
        } catch {
            if (mounted.current) setError("More Spaces could not be loaded. Please try again.");
        } finally {
            loadingMoreRef.current = false;
            if (mounted.current) setLoadingMore(false);
        }
    }, [cursor, applySnapshot]);
    useEffect(() => {
        // A batch may mostly contain duplicate legacy profiles. Keep filling the street.
        if (!loading && hasMore && !loadingMore && !error && users.length < PAGE_SIZE) loadMore();
    }, [loading, hasMore, loadingMore, error, users.length, loadMore]);
    const quality = QUALITY_PRESETS[qualityKey];
    const layout = useMemo(() => buildStreetLayout(users), [users]);
    const openUser = useCallback(user => navigate("/3d/space/" + encodeURIComponent(user.muultiName)), [navigate]);
    const findSpace = () => {
        const entry = layout.entries.find(item => item.user.id === search);
        if (entry) teleportRef.current = { x: entry.entranceX, z: entry.entranceZ, yaw: entry.entranceYaw, pitch: 0 };
    };

    if (loading) return <div className="muulti-container" role="status"><p>Loading 3D street...</p></div>;
    if (error && !users.length) return <div className="muulti-container" role="alert">
        <p>{error}</p><button className="muulti-button" onClick={() => setRetryKey(value => value + 1)}>Retry</button>
    </div>;
    return <>
        <SceneViewport label="MuultiSpace 3D street" qualityKey={qualityKey} setQualityKey={setQualityKey}
            shadowsOn={shadowsEnabled} setShadowsOn={setShadowsEnabled} lightingOn={lightingOn} setLightingOn={setLightingOn}
            bounds={layout.bounds} playerPosRef={playerPosRef} poseRef={poseRef} teleportRef={teleportRef} paused={!!selected}
            actions={<>
                <label>Find a Space<select value={search} onChange={event => setSearch(event.target.value)}>
                    <option value="">Choose a Space</option>
                    {users.map(user => <option key={user.id} value={user.id}>{user.pageTitle}</option>)}
                </select></label>
                <button className="muulti-button" disabled={!search} onClick={findSpace}>Go to entrance</button>
                <button className="muulti-button" disabled={!search} onClick={() => {
                    const user = users.find(item => item.id === search);
                    if (user) openUser(user);
                }}>Enter selected Space</button>
                {hasMore && <button className="muulti-button" onClick={loadMore} disabled={loadingMore}>{loadingMore ? "Loading..." : "Load more Spaces"}</button>}
                <button className="muulti-button" onClick={() => navigate("/muulti-directory")}>Open 2D directory</button>
            </>}
            overlay={<>
                {error && <p className="muulti-3d-notice" role="alert">{error}</p>}
                {loadingMore && users.length > 0 && <p className="muulti-3d-notice" role="status">Loading more Spaces...</p>}
                {!users.length && <p className="muulti-3d-notice" role="status">{hasMore ? "Loading more Spaces..." : "No Spaces are available yet."}</p>}
            </>}>
            <color attach="background" args={[NIGHT_SKY]} />
            <ambientLight intensity={lightingOn ? 0.32 : 0.18} />
            <hemisphereLight args={["#b5d5e2", MUULTI_GREEN, lightingOn ? 0.25 : 0.12]} />
            <StreetLight playerPosRef={playerPosRef} quality={quality} shadowsOn={shadowsEnabled} lightingOn={lightingOn} />
            <fog attach="fog" args={[NIGHT_SKY, quality.viewDistance * 0.55, quality.viewDistance]} />
            <Ground layout={layout} />
            <StreetBuildings layout={layout} quality={quality} playerPosRef={playerPosRef} lightingOn={lightingOn} onOpenUser={setSelected} onEnter={openUser} />
            <StreetDirectoryPrefetch enabled={hasMore && !loadingMore && !error && !selected}
                frontiers={layout.frontiers} playerPosRef={playerPosRef} distance={Math.min(160, quality.viewDistance * 0.35)} onNeedMore={loadMore} />
        </SceneViewport>
        {selected && <SceneDialog title={selected.pageTitle} onClose={() => setSelected(null)}>
            {selected.profilePicUrl && <img className="muulti-3d-feature-image" src={selected.profilePicUrl} alt={selected.pageTitle} />}
            <p className="muulti-3d-description">{selected.pageDescription}</p>
            <div className="muulti-3d-dialog-actions">
                <button className="muulti-button" onClick={() => openUser(selected)}>Enter 3D MuultiSpace</button>
                <button className="muulti-button" onClick={() => navigate("/" + encodeURIComponent(selected.muultiName))}>Open 2D MuultiSpace</button>
            </div>
        </SceneDialog>}
    </>;
}
